import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError} from "rxjs/operators";
import {Router} from "@angular/router";
import {AuthService} from "./auth.service";
import {AuthGuardService} from "./auth-guard.service";
import {isAuthEntryRequest} from "../utils/auth-entry-requests";
import {isGuardedUrl} from "../utils/guarded-routes";

@Injectable()
export class ApiResponseInterceptorService implements HttpInterceptor {

  /**
   * Guards against two parallel 401s each starting their own navigation. Clearing session
   * state idempotently is not enough on its own — both callbacks can still reach the router
   * before the first navigation resolves.
   */
  private navigatingToLogin = false;

  constructor(
    private authService: AuthService,
    private router: Router
  ) {
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (ApiResponseInterceptorService.isStaleSessionSignal(request, error)) {
          this.handleStaleSession();
        }
        // Always rethrow: component-level error handling still needs the original response.
        return throwError(() => error);
      })
    );
  }

  /**
   * A 401 means "this session is over" only when the request actually presented a session.
   *
   * Matching the literal message `jwt expired` — the previous rule — was brittle in one
   * direction and blind in the other: the API's error handler never emitted the `TOKEN_EXPIRED`
   * code that the other half of the condition looked for, and a 401 from any other protected
   * endpoint went unnoticed. Authentication-entry requests are excluded so that a mistyped
   * password on the login page is not mistaken for an expired session.
   */
  private static isStaleSessionSignal(request: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return error.status === 401
      && !isAuthEntryRequest(request)
      && request.headers.has('Authorization');
  }

  private handleStaleSession(): void {
    this.authService.expireSession();

    // On an intentionally public route the visitor stays put: the navbar reverts to anonymous
    // and their graph selections survive. Navigating away here would discard them.
    if (!isGuardedUrl(this.router.url, this.router.config, AuthGuardService)) {
      return;
    }

    if (this.navigatingToLogin) {
      return;
    }

    this.navigatingToLogin = true;
    const returnUrl = ApiResponseInterceptorService.safeReturnUrl(this.router.url);

    void this.router
      .navigate(['/login'], {queryParams: {returnUrl}})
      .then(
        () => (this.navigatingToLogin = false),
        () => (this.navigatingToLogin = false)
      );
  }

  private static safeReturnUrl(url: string): string | undefined {
    return url && url.startsWith('/') && !url.startsWith('//') ? url : undefined;
  }

}
